/**
 * MODULE: domain/pages/frontend/components/PageCommentThreadItem.tsx
 * PURPOSE: Shared comment row with resolve/reopen, copy link, and delete actions
 * RECENT: 2026-05-24 [2.2.1184] — copy-link timer ref typed as number (browser setTimeout). [2.2.1169] — ?comment= deep links; copy comment link button
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'flowbite-react';
import { HiOutlineCheckCircle, HiOutlineClipboardList, HiOutlineLink, HiOutlineRefresh, HiOutlineReply } from 'react-icons/hi';
import type { PageComment } from '../../contracts/pages.types';
import { CommentMentionContent } from './CommentMentionContent';
import { isCommentResolved } from '../utils/blockCommentUtils';
import { copyPageCommentLink } from '../utils/pageClipboardUtils';

type Props = {
  comment: PageComment;
  authorLabel: string;
  workspaceId?: string;
  pageId?: string;
  canResolve: boolean;
  canDelete: boolean;
  onResolve: (resolved: boolean) => void;
  onDelete: () => void;
  canCreateTask?: boolean;
  linkedTaskId?: string | null;
  onCreateTask?: () => void;
  createTaskPending?: boolean;
  resolvePending?: boolean;
  deletePending?: boolean;
  compact?: boolean;
  isReply?: boolean;
  testIdPrefix?: string;
  highlighted?: boolean;
  showReplyButton?: boolean;
  onReply?: () => void;
  replyOpen?: boolean;
  replyCount?: number;
};

export function PageCommentThreadItem({
  comment,
  authorLabel,
  workspaceId,
  pageId,
  canResolve,
  canDelete,
  onResolve,
  onDelete,
  canCreateTask = false,
  linkedTaskId = null,
  onCreateTask,
  createTaskPending = false,
  resolvePending = false,
  deletePending = false,
  compact = false,
  isReply = false,
  testIdPrefix = 'pages-comment',
  highlighted = false,
  showReplyButton = false,
  onReply,
  replyOpen = false,
  replyCount = 0,
}: Props) {
  const resolved = isCommentResolved(comment);
  const [linkCopied, setLinkCopied] = useState(false);
  const linkCopiedTimer = useRef<number | null>(null);
  const canCopyLink = Boolean(workspaceId && pageId);

  useEffect(() => {
    return () => {
      if (linkCopiedTimer.current) {
        window.clearTimeout(linkCopiedTimer.current);
      }
    };
  }, []);

  const handleCopyLink = useCallback(async () => {
    if (!workspaceId || !pageId) {
      return;
    }
    try {
      await copyPageCommentLink(workspaceId, pageId, comment.id, comment.block_id);
      setLinkCopied(true);
      if (linkCopiedTimer.current) {
        window.clearTimeout(linkCopiedTimer.current);
      }
      linkCopiedTimer.current = window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      window.alert('Could not copy link. Check browser clipboard permissions.');
    }
  }, [comment.block_id, comment.id, pageId, workspaceId]);

  return (
    <li
      className={`rounded-lg border p-3 transition-shadow duration-500 ${
        highlighted
          ? 'ring-2 ring-blue-400 ring-offset-2 dark:ring-blue-500 dark:ring-offset-gray-900'
          : ''
      } ${
        resolved
          ? 'border-emerald-100 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20'
          : 'border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'
      } ${compact ? 'p-2' : ''} ${isReply ? 'border-l-0 bg-transparent dark:bg-transparent' : ''}`}
      data-testid={`${testIdPrefix}-${comment.id}`}
      data-resolved={resolved ? 'true' : 'false'}
      data-highlighted={highlighted ? 'true' : 'false'}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{authorLabel}</p>
          {resolved ? (
            <span
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              data-testid={`${testIdPrefix}-resolved-badge-${comment.id}`}
            >
              <HiOutlineCheckCircle className="h-3 w-3" aria-hidden />
              Resolved
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          {showReplyButton && onReply ? (
            <Button
              size="xs"
              color={replyOpen ? 'blue' : 'light'}
              onClick={onReply}
              data-testid={`${testIdPrefix}-reply-${comment.id}`}
            >
              <HiOutlineReply className="mr-1 h-3 w-3" aria-hidden />
              {replyOpen ? 'Cancel reply' : replyCount > 0 ? `Reply (${replyCount})` : 'Reply'}
            </Button>
          ) : null}
          {canCopyLink ? (
            <Button
              size="xs"
              color="light"
              onClick={() => void handleCopyLink()}
              title="Copy link to this comment"
              data-testid={`${testIdPrefix}-copy-link-${comment.id}`}
            >
              <HiOutlineLink className="h-3 w-3" aria-hidden />
              <span className="sr-only">{linkCopied ? 'Link copied' : 'Copy comment link'}</span>
              {linkCopied ? (
                <span className="ml-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                  Copied
                </span>
              ) : null}
            </Button>
          ) : null}
          {linkedTaskId ? (
            <Link
              to="/tasks"
              className="inline-flex items-center rounded-lg bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-blue-800 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50"
              data-testid={`${testIdPrefix}-task-link-${comment.id}`}
            >
              In tasks
            </Link>
          ) : canCreateTask ? (
            <Button
              size="xs"
              color="light"
              onClick={onCreateTask}
              disabled={createTaskPending}
              title="Create CRM task from this comment"
              data-testid={`${testIdPrefix}-create-task-${comment.id}`}
            >
              <HiOutlineClipboardList className="mr-1 h-3 w-3" aria-hidden />
              {createTaskPending ? 'Adding…' : 'Add to tasks'}
            </Button>
          ) : null}
          {canResolve ? (
            <Button
              size="xs"
              color={resolved ? 'light' : 'success'}
              outline={resolved}
              onClick={() => onResolve(!resolved)}
              disabled={resolvePending}
              data-testid={`${testIdPrefix}-resolve-${comment.id}`}
            >
              {resolved ? (
                <>
                  <HiOutlineRefresh className="mr-1 h-3 w-3" aria-hidden />
                  Reopen
                </>
              ) : (
                'Resolve'
              )}
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              size="xs"
              color="light"
              onClick={onDelete}
              disabled={deletePending}
              data-testid={`${testIdPrefix}-delete-${comment.id}`}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>
      <div className={resolved ? 'opacity-80' : undefined}>
        <CommentMentionContent content={comment.content} />
      </div>
      {!compact ? (
        <p className="mt-1 text-xs text-gray-400">
          {new Date(comment.created_at).toLocaleString()}
        </p>
      ) : null}
    </li>
  );
}
